import * as React from "react";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import calculerSolde from "../libs/calculerSolde.js";
import quandEquilibre from "../libs/quandEquilibre.js";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import ContenuEtape from "./ContenuEtape";
let sizeH1 = "35px";
let sizeInfo = 12;

const titreEtape = [
  "Retrouvez l'équilibre financier",
  "Sauvegardez le niveau de vie des retraités",
  "Ajustez la durée de la retraite",
];

const messageExplicationProjection = [
  "Le Conseil d'orientation des retraites (COR) projette que le système actuel des réformes des retraites ne sera pas durablement à l'équilibre avant 2054. ",
  "Le niveau de vie des personnes à la retraite par rapport à l'ensemble de la population devrait être plus faible dans les décennies à venir, selon les projections du COR. Cela ne signifie pas que ce niveau de vie sera moindre dans l'absolu, mais uniquement comparé à celui du reste de la population et en particulier des actifs qui a tendance à augmenter plus vite. ",
  "En 2010, le gouvernement Fillon recule l'âge du départ légal de deux ans. La durée des retraites des actifs partis après a reculé significativement. Il faudra des dizaines d'années pour que la hausse de l'espérance de vie compense cette perte. ",
];

const messageExplicationAction = [
  "A votre tour de modifier les paramètres de votre réforme et voir l'impact que cela aurait sur l'équilibre financier.",
  "Vous pouvez jouer avec les différents paramètres de la réforme pour essayer de modérer cette différence de niveau de vie entre les retraités et le reste de la population.",
  "En agissant sur l'âge de départ, vous pourrez voir l'évolution de la durée de la retraite prévisionnelle, en prenant en compte l'évolution de l'espérance de vie que projette l'Insee.",
];

function App() {
  const baseAgeDepart = 62;
  const basePensionMoyenne = 1644;
  const baseNiveauCotisation = 30.5;

  const [contexteSocioEco, setContexteSocioEco] = React.useState("Neutre");
  const [niveauCotisationUtilisateur, setNiveauCotisationUtilisateur] =
    React.useState(baseNiveauCotisation);
  const [parametresModifies, setParametresModifies] = React.useState(0);
  const [ageDepart, setAgeDepart] = React.useState(baseAgeDepart);
  const [pensionMoyenne, setPensionMoyenne] =
    React.useState(basePensionMoyenne);
  const [activeStep, setActiveStep] = React.useState(0);
  const [skipped, setSkipped] = React.useState(new Set());

  const isStepOptional = (step) => {
    return false;
  };

  const isStepSkipped = (step) => {
    return skipped.has(step);
  };

  const handleNext = () => {
    let newSkipped = skipped;
    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(activeStep);
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped(newSkipped);
  };

  function handleChangeStep(stepTarget) {
    setActiveStep(stepTarget);
  }

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSkip = () => {
    if (!isStepOptional(activeStep)) {
      throw new Error("You can't skip a step that isn't optional.");
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped((prevSkipped) => {
      const newSkipped = new Set(prevSkipped.values());
      newSkipped.add(activeStep);
      return newSkipped;
    });
  };

  const handleReset = () => {
    setActiveStep(0);
    setAgeDepart(baseAgeDepart);
    setPensionMoyenne(basePensionMoyenne);
    setNiveauCotisationUtilisateur(baseNiveauCotisation);
    setParametresModifies(0);
  };

  const handleContexteSocioEco = (event, newContexteSocioEco) => {
    setContexteSocioEco(newContexteSocioEco);
  };
  const handleAgeDepart = (event) => {
    setAgeDepart(event.target.value);
    setParametresModifies(1);
    if (
      event.target.value == baseAgeDepart &&
      pensionMoyenne == basePensionMoyenne &&
      niveauCotisationUtilisateur == baseNiveauCotisation
    ) {
      setParametresModifies(0);
    }
  };

  const handleNiveauCotisationUtilisateur = (event) => {
    setNiveauCotisationUtilisateur(event.target.value);
    setParametresModifies(1);
    if (
      event.target.value == baseNiveauCotisation &&
      pensionMoyenne == basePensionMoyenne &&
      ageDepart == baseAgeDepart
    ) {
      setParametresModifies(0);
    }
  };

  const handlePensionMoyenne = (event) => {
    setPensionMoyenne(event.target.value);
    setParametresModifies(1);
    if (
      event.target.value == basePensionMoyenne &&
      niveauCotisationUtilisateur == baseNiveauCotisation &&
      ageDepart == baseAgeDepart
    ) {
      setParametresModifies(0);
    }
  };

  let texteEquilibreFinancier =
    "à l'équilibre en " +
    quandEquilibre(
      contexteSocioEco,
      niveauCotisationUtilisateur / 100,
      ageDepart,
      pensionMoyenne,
      baseAgeDepart,
      baseNiveauCotisation,
      basePensionMoyenne
    );
  if (
    calculerSolde(
      0,
      2070,
      contexteSocioEco,
      0,
      niveauCotisationUtilisateur / 100,
      ageDepart,
      pensionMoyenne,
      baseAgeDepart,
      baseNiveauCotisation,
      basePensionMoyenne
    ) < 0
  ) {
    texteEquilibreFinancier = "en déficit";
  }

  let texteNiveauVie = "plus bas";
  if (
    calculerSolde(
      1,
      2070,
      contexteSocioEco,
      0,
      niveauCotisationUtilisateur / 100,
      ageDepart,
      pensionMoyenne,
      baseAgeDepart,
      baseNiveauCotisation,
      basePensionMoyenne
    ) -
      calculerSolde(
        1,
        2070,
        contexteSocioEco,
        1,
        niveauCotisationUtilisateur / 100,
        ageDepart,
        pensionMoyenne,
        baseAgeDepart,
        baseNiveauCotisation,
        basePensionMoyenne
      ) >
    0
  ) {
    texteNiveauVie = "plus élevé";
  }

  
  let duree2022 = calculerSolde(
    2,
    2022,
    contexteSocioEco,
    0,
    niveauCotisationUtilisateur / 100,
    ageDepart,
    pensionMoyenne,
    baseAgeDepart,
    baseNiveauCotisation,
    basePensionMoyenne
  );
  let duree2070 = calculerSolde(
    2,
    2070,
    contexteSocioEco,
    0,
    niveauCotisationUtilisateur / 100,
    ageDepart,
    pensionMoyenne,
    baseAgeDepart,
    baseNiveauCotisation,
    basePensionMoyenne
  );

  const theme = createTheme({
    typography: {
      fontFamily: "Franklin Gothic",
    },
    palette: {
      primary: {
        main: "#ab2926",
      },
      info: {
        main: "#959da2",
      },
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <div className="">
        <Box sx={{ maxWidth: "800px" }}>
          <Stack spacing={4}>
            <Typography variant="h1" fontSize={sizeH1} id="debutEtape">
              Simulez votre propre réforme des retraites
              <Typography variant="h1" fontSize={sizeH1} color="primary">
                et observez son impact jusqu'en 2070
              </Typography>
            </Typography>

            <div>
              <Box
                sx={{
                  marginBottom: "40px",
                  paddingLeft: "5px",
                  paddingRight: "5px",
                }}
              >
                <ContenuEtape
                  baseAgeDepart={baseAgeDepart}
                  basePensionMoyenne={basePensionMoyenne}
                  baseNiveauCotisation={baseNiveauCotisation}
                  contexteSocioEco={contexteSocioEco}
                  niveauCotisationUtilisateur={niveauCotisationUtilisateur}
                  parametresModifies={parametresModifies}
                  ageDepart={ageDepart}
                  pensionMoyenne={pensionMoyenne}
                  activeStep={0}
                  _activeStep={titreEtape[0]}
                  __activeStep={messageExplicationProjection[0]}
                  ___activeStep={messageExplicationAction[0]}
                  isStepOptional={isStepOptional}
                  handleNext={handleNext}
                  handleBack={handleBack}
                  handleSkip={handleSkip}
                  handleReset={handleReset}
                  handleAgeDepart={handleAgeDepart}
                  handleNiveauCotisationUtilisateur={
                    handleNiveauCotisationUtilisateur
                  }
                  handlePensionMoyenne={handlePensionMoyenne}
                  texteEquilibreFinancier={texteEquilibreFinancier}
                  texteNiveauVie={texteNiveauVie}
                  duree2022={duree2022}
                  duree2070={duree2070}
                ></ContenuEtape>
              </Box>
              <Box
                sx={{
                  marginBottom: "40px",
                  paddingLeft: "5px",
                  paddingRight: "5px",
                }}
              >
                <ContenuEtape
                  baseAgeDepart={baseAgeDepart}
                  basePensionMoyenne={basePensionMoyenne}
                  baseNiveauCotisation={baseNiveauCotisation}
                  contexteSocioEco={contexteSocioEco}
                  niveauCotisationUtilisateur={niveauCotisationUtilisateur}
                  parametresModifies={parametresModifies}
                  ageDepart={ageDepart}
                  pensionMoyenne={pensionMoyenne}
                  activeStep={1}
                  _activeStep={titreEtape[1]}
                  __activeStep={messageExplicationProjection[1]}
                  ___activeStep={messageExplicationAction[1]}
                  isStepOptional={isStepOptional}
                  handleNext={handleNext}
                  handleBack={handleBack}
                  handleSkip={handleSkip}
                  handleReset={handleReset}
                  handleAgeDepart={handleAgeDepart}
                  handleNiveauCotisationUtilisateur={
                    handleNiveauCotisationUtilisateur
                  }
                  handlePensionMoyenne={handlePensionMoyenne}
                  texteEquilibreFinancier={texteEquilibreFinancier}
                  texteNiveauVie={texteNiveauVie}
                  duree2022={duree2022}
                  duree2070={duree2070}
                ></ContenuEtape>
              </Box>
              <Box sx={{ paddingLeft: "5px", paddingRight: "5px" }}>
                <ContenuEtape
                  baseAgeDepart={baseAgeDepart}
                  basePensionMoyenne={basePensionMoyenne}
                  baseNiveauCotisation={baseNiveauCotisation}
                  contexteSocioEco={contexteSocioEco}
                  niveauCotisationUtilisateur={niveauCotisationUtilisateur}
                  parametresModifies={parametresModifies}
                  ageDepart={ageDepart}
                  pensionMoyenne={pensionMoyenne}
                  activeStep={2}
                  _activeStep={titreEtape[2]}
                  __activeStep={messageExplicationProjection[2]}
                  ___activeStep={messageExplicationAction[2]}
                  isStepOptional={isStepOptional}
                  handleNext={handleNext}
                  handleBack={handleBack}
                  handleSkip={handleSkip}
                  handleReset={handleReset}
                  handleAgeDepart={handleAgeDepart}
                  handleNiveauCotisationUtilisateur={
                    handleNiveauCotisationUtilisateur
                  }
                  handlePensionMoyenne={handlePensionMoyenne}
                  handleReset={handleReset}
                  texteEquilibreFinancier={texteEquilibreFinancier}
                  texteNiveauVie={texteNiveauVie}
                  duree2022={duree2022}
                  duree2070={duree2070}
                ></ContenuEtape>
              </Box>
            </div>
          </Stack>
        </Box>
      </div>
    </ThemeProvider>
  );
}

export default App;
